import axios from "axios";
import useAuthStore from '../stores/auth-store.js';

const API_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000/";

// Create axios instance
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor - adds auth token to requests
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("authToken");
    if (token) {
      config.headers["Authorization"] = `Token ${token}`;
    }

    // Add client context header for Superadmin
    const { isSuperadmin, selectedClientId } = useAuthStore.getState();
    if (isSuperadmin() && selectedClientId) {
      config.headers['X-Client-Context'] = selectedClientId.toString();
      console.log(`🔄 Request with client context: ${selectedClientId}`);
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor with enhanced logging and delayed redirect
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Enhanced error logging
    console.group("API Error Details");
    console.error(
      "Request URL:",
      originalRequest.baseURL + originalRequest.url
    );
    console.error("Request Method:", originalRequest.method.toUpperCase());
    console.error("Status Code:", error.response?.status);
    console.error("Response Data:", error.response?.data);
    console.error("Request Headers:", originalRequest.headers);
    console.error("Full Error:", error);
    console.groupEnd();

    const status = error.response?.status;

    // Handle 403 Forbidden (Non-Superadmin using client context)
    if (status === 403) {
      const errorMessage = error.response?.data?.error || '';
      if (errorMessage.toLowerCase().includes('client context')) {
        const { clearClientContext } = useAuthStore.getState();
        clearClientContext();
        alert('⚠️ You do not have permission to switch client contexts.');
      }
    }

    // Handle 400 Bad Request (Invalid client ID)
    if (status === 400) {
      const errorMessage = error.response?.data?.error || '';
      if (errorMessage.toLowerCase().includes('invalid client')) {
        const { clearClientContext } = useAuthStore.getState();
        clearClientContext();
        alert('⚠️ Invalid client ID selected. Context cleared.');
      }
    }

    // Handle 401 Unauthorized
    if (
      status === 401 &&
      !originalRequest._retry
    ) {
      // Clear auth state if token is invalid
      const { clearAuth, setRedirect, clearClientContext } = useAuthStore.getState();
      clearAuth();
      clearClientContext();

      // Only redirect to login if not already on the login page
      const currentPath = window.location.pathname;
      if (
        !currentPath.includes("/login") &&
        !originalRequest.url.includes("token/")
      ) {
        setRedirect(
          currentPath,           // path
          null,                  // state
          "session_expired"      // reason
        );

        window.location.href = "/user/login";

        return Promise.reject(
          new Error("Session expired. Please login again.")
        );
      }
    }

    return Promise.reject(error);
  }
);

// Export the API client for use in other services
export default apiClient;